import { Box, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import getOodColor from '../../utils/getOodColor';
import { CheckOutlined } from '@mui/icons-material';
import { FragmentType, getFragmentData, graphql } from '../../gql';
import { Editor } from '../../components/wysiwyg/Editor';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';

const journeyTemplateTile_journeyFragment = graphql(`
	fragment journeyTemplateTile_journeyFragment on Journey {
		id
		name
		color
		description
	}
`);

type JourneyData = FragmentType<typeof journeyTemplateTile_journeyFragment>;

type Props = Readonly<{
	setSelectedJourneyId: (option: string) => void;
	setTemplateName: (templateName: string) => void;
	icon: JSX.Element;
	iconOnTop?: boolean;
	isActive?: boolean;
	journeyData: JourneyData;
}>;

const JourneyTemplateTile: FC<Props> = ({ setSelectedJourneyId, setTemplateName, icon, iconOnTop, isActive, journeyData }) => {
	const theme = useTheme();
	const journey = getFragmentData(journeyTemplateTile_journeyFragment, journeyData);

	const handleSelect = () => {
		setSelectedJourneyId(journey.id);
		setTemplateName(journey.name);
	};

	return (
		<Stack
			border={1}
			borderRadius={'6px'}
			borderColor={isActive ? theme.palette.primary.main : theme.palette.grey[300]}
			paddingX={2}
			paddingY={2}
			gap={2}
			width={'100%'}
			height={'100%'}
			position={'relative'}
			sx={{
				cursor: 'pointer',
				backgroundColor: theme.palette[getNightOrDayPalette(theme)][0],
				'&:hover': {
					borderColor: theme.palette.primary.main,
				},
			}}
			onClick={() => handleSelect()}
		>
			{isActive ? (
				<Box
					position={'absolute'}
					top={8}
					right={8}
					width={20}
					height={20}
					borderRadius={5}
					sx={{ backgroundColor: theme.palette.primary[400] }}
					display={'flex'}
					alignItems={'center'}
					justifyContent={'center'}
				>
					<CheckOutlined sx={{ width: '16px', height: '16px' }} htmlColor={theme.palette.grey[0]} />
				</Box>
			) : null}
			<Stack direction={iconOnTop ? 'column' : 'row'} justifyContent={'start'} alignItems={'start'} gap={2}>
				<Box
					width={'44px'}
					height={'44px'}
					borderRadius={'6px'}
					sx={{ backgroundColor: journey.color ? getOodColor(theme, journey.color).border : theme.palette.grey[200] }}
					display={'flex'}
					flexShrink={0}
					justifyContent={'center'}
					alignItems={'center'}
				>
					{icon}
				</Box>
				<Stack textAlign={'left'} gap={0.3}>
					<Typography>{journey.name}</Typography>
					<Box
						lineHeight={'20px'}
						fontSize={'12px'}
						sx={{
							display: '-webkit-box',
							overflow: 'hidden',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
						}}
					>
						{journey.description && <Editor readonly state={journey.description} />}
					</Box>
				</Stack>
			</Stack>
		</Stack>
	);
};

export { JourneyTemplateTile };
